import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  addTranscriptionSegments,
  stopRecording as stopRecordingAPI,
  sendRecordingHeartbeat,
  getRealtimeToken,
} from "@/services/recording.service";
import audioRecorderService from "@/services/AudioRecorderService";
import ProxyUploadService from "@/services/ProxyUploadService";
import AssemblyAIWebSocketService from "@/services/AssemblyAIWebSocketService";
import { liveStreamingConfig } from "@/constants/liveTranscription";
import criticalLoggingService from "@/services/CriticalLoggingService";
import recordingAnalytics, {
  isPermissionError,
} from "@/services/recording-analytics";

export const useRecordingStore = defineStore("recording", () => {
  // Recording state
  const activeRecording = ref(null);
  const isRecording = ref(false);
  const isPaused = ref(false);
  const recordingDuration = ref(0);
  const currentAudioLevel = ref(0);

  // Transcription state
  const transcriptionSegments = ref([]);
  const currentInterimText = ref("");
  const pendingSegments = ref([]);

  // Audio recording state
  const audioChunks = ref([]);
  const hasMediaRecorder = ref(false); // Track if we have an active MediaRecorder

  // Upload state
  const uploadUrl = ref(null);
  const uploadedBytes = ref(0);
  const isUploading = ref(false);

  // WebSocket state
  const assemblyaiToken = ref(null);

  // Live-transcription reconnection state
  const isReconnectingTranscription = ref(false);
  // Offset applied to AssemblyAI turn_order values. Each streaming session
  // restarts turn_order at 0; on reconnect we bump this base past the highest
  // turn already captured so a new session's turns never overwrite earlier
  // transcript (segments are keyed by turnOrder).
  let turnOrderBase = 0;

  // Transcription language for the live preview (picks the speech model).
  // The server's /status `language` wins; until the API sends it, the Record
  // popup hands the chosen language over in this single slot.
  let languageHandoff = null; // { audioFileId, language }
  let liveLanguage = null;
  // Live-preview analytics: 0-based index of the recording's next AssemblyAI
  // socket (allocated per socket, so failed attempts count, as on Android).
  let nextLiveSessionIndex = 0;

  // Recording generation for the live preview. Every socket listener and
  // queued segment sync captures it; anything from an older generation (a
  // reset, or a new recording) is ignored. Not bumped by stop, which must
  // still receive the final turn.
  let liveGen = 0;
  // Single-flight stop, per recording: { gen, promise }. A stop of an earlier
  // recording still waiting on its API response never affects a newer one.
  let stopInFlight = null;
  const stoppingThisRecording = (gen = liveGen) => stopInFlight?.gen === gen;
  // The page was hidden (pagehide) and not shown again.
  let pageSuspended = false;
  // Serialized segment syncs (see syncTranscriptionSegments).
  let segmentSyncChain = Promise.resolve();

  // Services
  let uploadService = null;
  let assemblyAIService = null;

  // In-flight chunk uploads, drained before finalizing on stop
  let pendingChunkUploads = new Set();

  // Timer
  let durationInterval = null;
  let segmentSyncInterval = null;
  let heartbeatInterval = null;

  // Recording limits
  const recordingLimits = ref({
    maxDurationSeconds: 600,
    remainingSeconds: 600,
    remainingFiles: 3,
    enforceRealtimeLimits: false,
  });

  // Warning states to prevent duplicate notifications
  const warningShown = ref({
    twoMinutes: false,
    oneMinute: false,
    thirtySeconds: false,
  });

  // Computed
  const hasActiveRecording = computed(() => activeRecording.value !== null);
  const recordingStatus = computed(
    () => activeRecording.value?.status || "idle",
  );
  const uploadProgress = computed(() => {
    if (!uploadService || uploadedBytes.value === 0) return 0;
    return uploadService.getProgress();
  });

  // Methods
  const rememberLiveLanguage = (audioFileId, language) => {
    languageHandoff = { audioFileId: String(audioFileId), language };
  };

  const setActiveRecording = async (recording) => {
    console.log("[Recording Store] Setting active recording:", recording);
    const id = String(recording.audioFileId);
    const sameRecording =
      activeRecording.value && String(activeRecording.value.audioFileId) === id;
    if (recording.language) {
      liveLanguage = recording.language;
    } else if (languageHandoff?.audioFileId === id) {
      liveLanguage = languageHandoff.language || null;
    } else if (!sameRecording) {
      liveLanguage = null;
    }
    if (languageHandoff?.audioFileId === id) languageHandoff = null;
    activeRecording.value = recording;
    uploadUrl.value = recording.gcsUploadUrl;
    assemblyaiToken.value = recording.assemblyaiToken;

    console.log("=== [STATE_LOG] ACTIVE_RECORDING_SET ===");
    console.log("ACTIVE_RECORDING_SET_STATE:", {
      timestamp: new Date().toISOString(),
      audioFileId: recording.audioFileId,
      status: recording.status,
      hasActiveRecording: true,
      hasMediaRecorder: hasMediaRecorder.value,
      isRecording: isRecording.value,
      recordingDuration: recordingDuration.value,
    });

    // Set recording limits from backend response
    if (recording.maxDurationSeconds !== undefined) {
      recordingLimits.value = {
        maxDurationSeconds: recording.maxDurationSeconds,
        remainingSeconds:
          recording.remainingSeconds || recording.maxDurationSeconds,
        remainingFiles: recording.remainingFiles || 3,
        enforceRealtimeLimits: recording.enforceRealtimeLimits || false,
      };

      // Reset warning states for new recording
      warningShown.value = {
        twoMinutes: false,
        oneMinute: false,
        thirtySeconds: false,
      };
    }

    console.log("[Recording Store] Upload URL:", uploadUrl.value);
    console.log(
      "[Recording Store] AssemblyAI Token:",
      assemblyaiToken.value ? "Present" : "Missing",
    );
    console.log("[Recording Store] Recording limits:", recordingLimits.value);
    console.log(
      "[Recording Store] Enforce realtime limits:",
      recordingLimits.value.enforceRealtimeLimits,
    );

    // Initialize proxy upload service
    console.log("[Recording Store] Initializing proxy upload service...");
    uploadService = new ProxyUploadService(recording.audioFileId);
  };

  const startRecording = async () => {
    console.log("[Recording Store] Starting recording process...");
    try {
      if (!activeRecording.value) {
        console.error("[Recording Store] No active recording session");
        throw new Error("No active recording session");
      }

      console.log("[Recording Store] Active recording:", activeRecording.value);

      // Reset in-flight chunk upload tracking for this recording
      pendingChunkUploads = new Set();

      // Clear any leftover state from a PREVIOUS recording. The store is a
      // singleton that survives SPA navigation, and cleanup() (on view unmount)
      // intentionally only stops timers — so without this, a record -> stop ->
      // Home -> Record-again flow (no page refresh) would show the prior
      // recording's transcript and a non-reset timer. This is the fresh-start
      // path only; orphaned-session recovery returns before startRecording, so
      // its buffered data is never cleared here.
      transcriptionSegments.value = [];
      currentInterimText.value = "";
      pendingSegments.value = [];
      recordingDuration.value = 0;
      currentAudioLevel.value = 0;
      audioChunks.value = [];
      uploadedBytes.value = 0;
      isReconnectingTranscription.value = false;
      turnOrderBase = 0;
      const gen = ++liveGen;
      // This recording now owns the store. Anything an earlier recording left
      // running is retired here — its stop (if still finishing) leaves shared
      // state to the owner, so the owner must clean it up: sync work, timers,
      // page listeners, and its live session (which would otherwise stay open
      // and billed even if this start fails below).
      retireSegmentSyncs();
      stopTimers();
      disconnectFromAssemblyAI();
      removePageLifecycleListeners();
      pageSuspended = false;
      nextLiveSessionIndex = 0;

      // Initialize audio recorder
      console.log("[Recording Store] Initializing audio recorder...");
      // NOTE: permission requested/granted are emitted by RecordingPopup, which
      // owns the real first-time permission gate. Emitting them here too would
      // double-count every successful recording. The classified catch below
      // remains as a backstop for an in-flight denial.
      await audioRecorderService.initialize();
      console.log("[Recording Store] Audio recorder initialized successfully");
      if (gen !== liveGen) {
        // Reset (or another recording) while the microphone was opening.
        console.warn("[Recording Store] Start superseded; not recording");
        return;
      }

      // (Chunk Content-Type is labeled from the recorder's actual MIME type
      // AFTER start() below — the MediaRecorder only populates mimeType once
      // start() runs, so reading it here would be empty for Safari/default.)

      // Set up event listeners
      console.log("[Recording Store] Setting up event listeners...");
      audioRecorderService.setListeners({
        onChunk: async (chunk) => {
          const chunkNumber = audioChunks.value.length + 1;
          console.log(
            "[Recording Store] Received audio chunk:",
            chunk.size,
            "bytes",
            "Total chunks:",
            chunkNumber,
          );
          audioChunks.value.push(chunk);

          // Log first chunk with UI state snapshot
          if (chunkNumber === 1) {
            criticalLoggingService.logUIStateSnapshot("first_chunk_received", {
              audioFileId: activeRecording.value?.audioFileId,
              chunk: {
                size: chunk.size,
                type: chunk.type,
                chunkNumber: chunkNumber,
              },
              mediaRecorder: {
                hasMediaRecorder: hasMediaRecorder.value,
                state: audioRecorderService?.getState(),
                isRecording: isRecording.value,
                isPaused: isPaused.value,
              },
              store: {
                recordingDuration: recordingDuration.value,
                currentAudioLevel: currentAudioLevel.value,
                audioChunks: audioChunks.value.length,
                uploadedBytes: uploadedBytes.value,
                isUploading: isUploading.value,
              },
              uploadService: {
                hasUploadService: !!uploadService,
                uploadedBytes: uploadService?.uploadedBytes || 0,
                totalBytes: uploadService?.totalBytes || 0,
                chunksLength: uploadService?.chunks?.length || 0,
              },
            });
          }

          // Log upload service state before uploading
          console.log(
            "[Recording Store] Upload service state before chunk upload:",
            {
              hasUploadService: !!uploadService,
              uploadedBytes: uploadService?.uploadedBytes || 0,
              totalBytes: uploadService?.totalBytes || 0,
              chunksLength: uploadService?.chunks?.length || 0,
            },
          );

          const _p = uploadChunk(chunk);
          pendingChunkUploads.add(_p);
          _p.finally(() => pendingChunkUploads.delete(_p));
          await _p;

          // Log upload service state after uploading
          const afterUploadState = {
            uploadedBytes: uploadService?.uploadedBytes || 0,
            totalBytes: uploadService?.totalBytes || 0,
            chunksLength: uploadService?.chunks?.length || 0,
          };
          console.log(
            "[Recording Store] Upload service state after chunk upload:",
            afterUploadState,
          );

          // Log successful chunk upload
          criticalLoggingService.logChunkUpload("success", {
            audioFileId: activeRecording.value?.audioFileId,
            chunkSize: chunk.size,
            chunkIndex: audioChunks.value.length,
            uploadedBytes: uploadService?.uploadedBytes || 0,
            totalBytes: uploadService?.totalBytes || 0,
          });
        },
        onAudioLevel: (level) => {
          currentAudioLevel.value = level;
        },
        onAudioData: (pcmData) => {
          // Send audio data to AssemblyAI
          if (assemblyAIService && assemblyAIService.isConnected) {
            console.log(
              "[Recording Store] Sending PCM data to AssemblyAI:",
              pcmData.byteLength,
              "bytes",
            );
            assemblyAIService.sendAudio(pcmData);
          } else {
            console.warn(
              "[Recording Store] Cannot send audio - AssemblyAI not connected:",
              {
                hasService: !!assemblyAIService,
                isConnected: assemblyAIService?.isConnected,
              },
            );
          }
        },
        onError: (error) => {
          console.error("[Recording Store] Recording error:", error);
          // Previously console-only, so a mid-recording failure was invisible in
          // analytics. This does NOT stop the recording — behaviour unchanged.
          recordingAnalytics.failed(activeRecording.value?.audioFileId, {
            error,
            phase: "recorder",
            code: "mediarecorder_error",
            durationBeforeFailure: recordingDuration.value,
          });
          // The recorder's state settles after the error event.
          setTimeout(endLivePreviewIfCaptureEnded, 0);
        },
        onStop: (audioBlob) => {
          console.log(
            "[Recording Store] Recording stopped, final blob:",
            audioBlob.size,
            "bytes",
          );
          endLivePreviewIfCaptureEnded();
        },
      });

      // Start recording
      console.log("[Recording Store] Starting audio recorder...");
      audioRecorderService.start();

      // Label chunk uploads with the recorder's ACTUAL MIME type so the
      // Content-Type matches the real bytes (Safari records audio/mp4, not
      // webm). Read AFTER start() — the MediaRecorder only populates mimeType
      // once start() runs (it's empty beforehand on the default/Safari path),
      // and this runs synchronously before the first ~500ms dataavailable.
      if (uploadService) {
        uploadService.setContentType(audioRecorderService.getMimeType());
      }

      // Verify the MediaRecorder actually started
      const mediaRecorderState = audioRecorderService.getState();
      console.log(
        "[Recording Store] MediaRecorder state after start:",
        mediaRecorderState,
      );

      // Log MediaRecorder state
      criticalLoggingService.logMediaRecorderState("start_attempt", {
        audioFileId: activeRecording.value.audioFileId,
        expectedState: "recording",
        actualState: mediaRecorderState,
        isSuccess: mediaRecorderState === "recording",
      });

      if (mediaRecorderState !== "recording") {
        const error = new Error(
          `MediaRecorder failed to start. State: ${mediaRecorderState}`,
        );

        // Log MediaRecorder failure
        criticalLoggingService.logMediaRecorderState("start_failed", {
          audioFileId: activeRecording.value.audioFileId,
          expectedState: "recording",
          actualState: mediaRecorderState,
          isError: true,
          error: error.message,
        });

        throw error;
      }

      isRecording.value = true;
      isPaused.value = false;
      hasMediaRecorder.value = true; // We now have an active MediaRecorder

      console.log("=== [STATE_LOG] MEDIA_RECORDER_CREATED ===");
      const stateSnapshot = {
        timestamp: new Date().toISOString(),
        hasMediaRecorder: hasMediaRecorder.value,
        isRecording: isRecording.value,
        isPaused: isPaused.value,
        activeRecording: activeRecording.value?.audioFileId,
        recordingDuration: recordingDuration.value,
      };
      console.log("MEDIA_RECORDER_STATE:", stateSnapshot);

      // Log UI state snapshot when MediaRecorder is created
      criticalLoggingService.logUIStateSnapshot("media_recorder_created", {
        audioFileId: activeRecording.value?.audioFileId,
        mediaRecorder: {
          hasMediaRecorder: hasMediaRecorder.value,
          state: audioRecorderService.getState(),
          isRecording: isRecording.value,
          isPaused: isPaused.value,
        },
        store: {
          recordingDuration: recordingDuration.value,
          currentAudioLevel: currentAudioLevel.value,
          audioChunks: audioChunks.value.length,
          uploadedBytes: uploadedBytes.value,
          isUploading: isUploading.value,
          transcriptionSegments: transcriptionSegments.value.length,
        },
        services: {
          hasUploadService: !!uploadService,
          hasAssemblyAIService: !!assemblyAIService,
          uploadUrl: !!uploadUrl.value,
          assemblyaiToken: !!assemblyaiToken.value,
        },
      });

      console.log("[Recording Store] Recording started, starting timers...");
      startTimers();

      console.log("[Recording Store] Connecting to AssemblyAI...");
      connectToAssemblyAI();
      addPageLifecycleListeners();

      // Start segment sync
      console.log("[Recording Store] Starting segment sync...");
      startSegmentSync();

      // Start heartbeat for free users
      if (recordingLimits.value.enforceRealtimeLimits) {
        console.log(
          "[Recording Store] Starting heartbeat for limit enforcement...",
        );
        startHeartbeat();
      }

      console.log("[Recording Store] Recording initialization complete!");
      recordingAnalytics.started(
        activeRecording.value?.audioFileId,
        audioRecorderService.chunkDuration,
      );
    } catch (error) {
      console.error("[Recording Store] Failed to start recording:", error);
      // Classify: only permission-class rejections are denials. getUserMedia
      // also rejects with NotFoundError (no mic), NotReadableError (device
      // busy) and AbortError — counting those as denials would corrupt the
      // permission funnel with hardware failures.
      if (isPermissionError(error)) {
        recordingAnalytics.permissionDenied(error);
      } else {
        recordingAnalytics.failed(activeRecording.value?.audioFileId, {
          error,
          phase: "start",
          durationBeforeFailure: recordingDuration.value,
        });
      }
      throw error;
    }
  };

  const pauseRecording = async () => {
    audioRecorderService.pause();
    recordingAnalytics.paused(
      activeRecording.value?.audioFileId,
      recordingDuration.value,
    );
    isPaused.value = true;
    isRecording.value = false;

    // End the live-transcription session while paused: AssemblyAI bills for
    // the time a session is open, and terminating also delivers the last turn.
    // Resume re-establishes it with a fresh token. This never affects the
    // saved recording or the final transcript.
    isReconnectingTranscription.value = false;
    if (assemblyAIService) {
      assemblyAIService.terminate();
    }
  };

  // Returns whether the recording actually resumed. A resume that lands after
  // a stop (e.g. its API call outlived an auto-stop) is ignored: the recorder
  // is no longer paused, so nothing changes and no live session opens.
  const resumeRecording = async () => {
    if (
      stoppingThisRecording() ||
      audioRecorderService.getState() !== "paused"
    ) {
      console.warn("[Recording Store] Resume ignored: recorder is not paused");
      return false;
    }
    audioRecorderService.resume();
    recordingAnalytics.resumed(
      activeRecording.value?.audioFileId,
      recordingDuration.value,
    );
    isPaused.value = false;
    isRecording.value = true;

    // Re-establish live transcription. reconnect() waits for the paused
    // session to finish winding down (keeping its last turn), then connects
    // with a fresh token. Best-effort and never rejects: if it fails, audio
    // upload and the final server-side transcript are unaffected.
    // (The reconnecting indicator is driven by the service's callbacks, so a
    // reconnect that bails early never leaves it on.)
    if (assemblyAIService) {
      assemblyAIService.reconnect();
    } else {
      connectToAssemblyAI();
    }
    return true;
  };

  // `stopReason` is one of: "user" | "limit_reached" | "navigation" | "unknown".
  // Was a boolean isAutoStop; widened so telemetry can tell a user stop from a
  // navigation stop. NOTE the auto-stop branch below must compare to the enum
  // value, not truthiness — every non-empty string is truthy.
  const STOP_REASONS = new Set([
    "user",
    "limit_reached",
    "navigation",
    "unknown",
  ]);

  // Single-flight: a second stop (e.g. the limit heartbeat racing a user
  // stop) joins the one in progress instead of finalizing twice.
  const stopRecording = (rawStopReason = "unknown") => {
    if (!stoppingThisRecording()) {
      const entry = { gen: liveGen, promise: null };
      entry.promise = runStop(rawStopReason).finally(() => {
        if (stopInFlight === entry) stopInFlight = null;
      });
      stopInFlight = entry;
    }
    return stopInFlight.promise;
  };

  const runStop = async (rawStopReason) => {
    // Normalise defensively: a stale caller passing the old boolean `true` would
    // otherwise emit an out-of-contract stop_reason. Behaviour still keys off
    // the enum comparison below, never truthiness.
    const stopReason = STOP_REASONS.has(rawStopReason)
      ? rawStopReason
      : "unknown";
    if (stopReason !== rawStopReason) {
      console.warn("[Recording Store] Unknown stopReason:", rawStopReason);
    }
    // Everything after the first await uses this snapshot, so a reset or a
    // new recording meanwhile can't redirect this stop's effects.
    const target = {
      audioFileId: activeRecording.value?.audioFileId,
      audioFileUUID: activeRecording.value?.audioFileUUID,
      gen: liveGen,
    };
    // False once a reset or a new recording took over the store while this
    // stop was still running: this stop then leaves the shared live-preview
    // state (page listeners, pending segments, sync queue) to its new owner.
    const stillOwnsPreview = () => target.gen === liveGen;
    // Duration must be snapshotted BEFORE teardown (timers are cleared below,
    // and a newer recording resets it).
    const stoppedDurationSeconds = recordingDuration.value;
    try {
      // Update UI state immediately
      isRecording.value = false;
      isPaused.value = false;
      hasMediaRecorder.value = false; // MediaRecorder is being stopped

      console.log("=== [STATE_LOG] MEDIA_RECORDER_STOPPED ===");
      console.log("MEDIA_RECORDER_STOPPED_STATE:", {
        timestamp: new Date().toISOString(),
        hasMediaRecorder: hasMediaRecorder.value,
        isRecording: isRecording.value,
        isPaused: isPaused.value,
        stopReason: stopReason,
        activeRecording: target.audioFileId,
        recordingDuration: recordingDuration.value,
      });

      // Stop the audio recorder (resolves after the final dataavailable chunk)
      await audioRecorderService.stop();

      // ...but chunk count must be read AFTER the await: stop() resolves only
      // once the final dataavailable callback has appended the last chunk.
      const stoppedChunkCount = audioChunks.value.length;

      recordingAnalytics.stopped(target.audioFileId, {
        durationSeconds: stoppedDurationSeconds,
        chunkCount: stoppedChunkCount,
        stopReason,
      });

      // End the live-transcription session. It runs alongside the upload
      // drain below and is awaited before the final segment sync, so the last
      // turn AssemblyAI sends back is included. Bounded (~3 s), never throws.
      // Timers and the live session are shared store state: if a newer
      // recording already took over, they are its, not this stop's.
      let liveTranscriptionEnded = Promise.resolve();
      if (stillOwnsPreview()) {
        stopTimers();
        liveTranscriptionEnded = terminateAssemblyAI();
        isReconnectingTranscription.value = false;
      }

      // Drain any in-flight chunk uploads before finalizing
      await Promise.allSettled([...pendingChunkUploads]);

      // Finalize upload — BEST EFFORT. Uploading the last buffered chunks can
      // fail (e.g. the free-tier meter returns 402 right at the cap, or a
      // transient network error). This MUST NOT abort the stop: the backend
      // /stop call below composes whatever chunks already reached GCS, so the
      // recording is still saved. Previously a throw here skipped the /stop
      // call, left the session stuck in RECORDING, and lost the whole file.
      let finalizeOk = true;
      try {
        await finalizeUpload();
      } catch (finalizeError) {
        finalizeOk = false;
        console.error(
          "[Recording Store] finalizeUpload failed; finalizing on backend with chunks already uploaded",
          finalizeError,
        );
        criticalLoggingService.logError(finalizeError, {
          audioFileId: target.audioFileId,
          context: "finalize_upload_nonfatal_on_stop",
          stopReason,
          uploadedBytes: uploadService?.uploadedBytes || 0,
          totalBytes: uploadService?.totalBytes || 0,
        });
      }

      // Read now: a recording started after this point resets these.
      const stoppedUploadedBytes = uploadedBytes.value;

      await liveTranscriptionEnded;

      // Send final transcription segments: waits (bounded) for any sync in
      // flight, then sends everything pending as final. Never throws and never
      // blocks saving the audio for long.
      if (stillOwnsPreview()) {
        removePageLifecycleListeners();
        await flushTranscriptionSegmentsForStop(target);
      }

      // Stop recording on backend — composes uploaded chunks into the final file
      const { data, error, status } = await stopRecordingAPI(
        target.audioFileId,
        `realTimeRecordings/completed/${target.audioFileUUID}.webm`,
        stoppedDurationSeconds,
        stoppedUploadedBytes,
      );

      if (error) {
        throw new Error(error.message || "Failed to stop recording");
      }

      // If this is an auto-stop, emit a special event for navigation
      if (stopReason === "limit_reached") {
        globalThis.dispatchEvent(
          new CustomEvent("recording-auto-stopped", {
            detail: {
              audioFileUUID: target.audioFileUUID,
              reason: "Recording limit reached",
            },
          }),
        );
      }

      const stopOutcome = {
        durationSeconds: stoppedDurationSeconds,
        finalizeOk,
        uploadedBytes: stoppedUploadedBytes,
      };
      recordingAnalytics.stopAccepted(target.audioFileId, stopOutcome);
      // 200/201 = the synchronous composed body — durable as observed by this
      // client. 202 = accepted only; composition is async and completed must
      // NOT fire in-process. If the envelope ever stops carrying status,
      // under-emitting completed is the safe failure mode, never over-emitting.
      if (status === 200 || status === 201) {
        recordingAnalytics.completed(target.audioFileId, stopOutcome);
      }

      return data;
    } catch (error) {
      console.error("Failed to stop recording:", error);
      recordingAnalytics.failed(target.audioFileId, {
        error,
        phase: "stop",
        durationBeforeFailure: stoppedDurationSeconds,
      });
      throw error;
    }
  };

  // Fetch a fresh AssemblyAI realtime token from the backend. Used by the
  // socket service to reconnect (the initial token is short-lived). Returns null
  // on failure so the service can fall back to its backoff/retry path.
  const fetchRealtimeToken = async () => {
    if (!activeRecording.value) return null;
    try {
      const { data, error } = await getRealtimeToken(
        activeRecording.value.audioFileId,
      );
      if (error) {
        console.error(
          "[Recording Store] Failed to fetch realtime token:",
          error,
        );
        return null;
      }
      return data?.token || null;
    } catch (error) {
      console.error("[Recording Store] Realtime token request threw:", error);
      return null;
    }
  };

  // Compute the turn_order offset for a newly (re)connected streaming session so
  // its turns (which restart at 0) are appended past the transcript already
  // captured, rather than overwriting it.
  const nextTurnOrderBase = () => {
    if (transcriptionSegments.value.length === 0) return 0;
    const maxTurnOrder = transcriptionSegments.value.reduce(
      (max, s) => (s.turnOrder > max ? s.turnOrder : max),
      -1,
    );
    return maxTurnOrder + 1;
  };

  // The live preview is wanted only while THIS recording's recorder is
  // actually recording. Every socket open is gated on it, so no late
  // continuation (a resume after stop, a start after reset) can open a billed
  // session.
  const liveSessionWanted = (gen) => () =>
    gen === liveGen &&
    !stoppingThisRecording(gen) &&
    !pageSuspended &&
    audioRecorderService.getState() === "recording";

  // Capture ended without a stop from us (the browser stopped the recorder,
  // a device was unplugged, a recorder error): an open session would keep
  // billing with no audio, so end it. A stop in progress ends it itself,
  // after waiting for the final turn.
  const endLivePreviewIfCaptureEnded = () => {
    if (stoppingThisRecording()) return;
    const state = audioRecorderService.getState();
    if (state === "recording" || state === "paused") return;
    isReconnectingTranscription.value = false;
    terminateAssemblyAI();
  };

  // Best-effort: never throws. Failures go to the service's retry owner.
  const connectToAssemblyAI = () => {
    if (!assemblyaiToken.value) {
      console.error("No AssemblyAI token available");
      return;
    }
    // Never two services: end any previous one first.
    disconnectFromAssemblyAI();

    const gen = liveGen;
    // Captured once for this service, like Android's base payload.
    const audioFileId = activeRecording.value?.audioFileId;
    const { speechModel, proLanguage } = liveStreamingConfig(liveLanguage);
    const baseSessionProperties = {
      speech_model: speechModel,
      live_language: proLanguage ?? "auto",
      recording_language: liveLanguage ?? "unspecified",
    };
    const sessionProperties = ({ sessionIndex, sessionId }) => ({
      ...baseSessionProperties,
      session_index: sessionIndex,
      aai_session_id: sessionId ?? null,
    });
    const seconds = (value) =>
      typeof value === "number" && Number.isFinite(value) ? value : null;
    const current =
      (handler) =>
      (...args) => {
        if (gen === liveGen) handler(...args);
      };

    // Create AssemblyAI service with a token provider so it can mint a fresh
    // token whenever it needs to reconnect.
    assemblyAIService = new AssemblyAIWebSocketService(assemblyaiToken.value, {
      tokenProvider: fetchRealtimeToken,
      language: liveLanguage,
      isWanted: liveSessionWanted(gen),
      nextSessionIndex: () => (gen === liveGen ? nextLiveSessionIndex++ : null),
    });

    // Set up event listeners. All of them are dropped once the recording
    // generation changes (reset / a new recording).
    assemblyAIService.setListeners({
      // AssemblyAI's Begin: a (billed) session started.
      onSessionBegins: current((message, session) => {
        recordingAnalytics.liveSessionStarted(
          audioFileId,
          sessionProperties(session),
        );
      }),
      // AssemblyAI's Termination: that session ended (billing stopped).
      onSessionTerminated: current((message, session) => {
        recordingAnalytics.liveSessionEnded(audioFileId, {
          ...sessionProperties(session),
          session_duration_seconds: seconds(message?.session_duration_seconds),
          audio_duration_seconds: seconds(message?.audio_duration_seconds),
        });
      }),
      onTranscript: current((transcript) => {
        addTranscriptionSegment(transcript);
      }),
      onError: (error) => {
        console.error("AssemblyAI error:", error);
      },
      onConnect: current(() => {
        console.log("Connected to AssemblyAI");
        // A streaming session is (re)starting. Earlier sessions can never
        // update their turns again, so seal them; then offset incoming
        // turn_order so this session can't overwrite earlier transcript.
        finalizeEarlierSessionSegments();
        turnOrderBase = nextTurnOrderBase();
        isReconnectingTranscription.value = false;
      }),
      onDisconnect: () => {
        console.log("Disconnected from AssemblyAI");
      },
      onReconnecting: current((attempt) => {
        console.log(
          `[Recording Store] Reconnecting live transcription (attempt ${attempt})`,
        );
        isReconnectingTranscription.value = true;
      }),
      onReconnected: current(() => {
        console.log("[Recording Store] Live transcription reconnected");
        isReconnectingTranscription.value = false;
      }),
      onReconnectFailed: current(() => {
        console.error("[Recording Store] Live transcription gave up");
        isReconnectingTranscription.value = false;
      }),
    });

    assemblyAIService.start();
  };

  // Graceful end (stop, or capture ending on its own): see
  // AssemblyAIWebSocketService.terminate(). One shared drain: a stop that
  // arrives while a drain is already running waits for that same drain (and
  // so still gets the final turn). The draining service stays reachable so
  // reset() can hard-close it.
  let liveDrain = null; // { service, promise }
  const terminateAssemblyAI = () => {
    const service = assemblyAIService;
    if (!service) return liveDrain ? liveDrain.promise : Promise.resolve();
    assemblyAIService = null;
    const drain = { service, promise: null };
    drain.promise = service.terminate().finally(() => {
      if (liveDrain === drain) liveDrain = null;
    });
    liveDrain = drain;
    return drain.promise;
  };

  // The page is going away: end the session now (Terminate, no waiting). The
  // service is kept so a page restored from the back/forward cache can
  // reconnect it.
  const handlePageHide = () => {
    // Set first: until the page is shown again, nothing may open a session
    // (e.g. a late resume continuation starting a fresh reconnect).
    pageSuspended = true;
    isReconnectingTranscription.value = false;
    if (assemblyAIService) assemblyAIService.disconnect();
  };

  // Restored from the back/forward cache: reconnect with a fresh token. The
  // service's isWanted gate keeps it closed unless still recording.
  const handlePageShow = (event) => {
    pageSuspended = false;
    if (event?.persisted && assemblyAIService) {
      assemblyAIService.reconnect();
    }
  };

  // Registered by the store (not the view) because a recording keeps running
  // across in-app navigation, after the recording view has unmounted.
  let pageListenersAdded = false;
  const addPageLifecycleListeners = () => {
    if (pageListenersAdded) return;
    globalThis.addEventListener?.("pagehide", handlePageHide);
    globalThis.addEventListener?.("pageshow", handlePageShow);
    pageListenersAdded = true;
  };
  const removePageLifecycleListeners = () => {
    if (!pageListenersAdded) return;
    globalThis.removeEventListener?.("pagehide", handlePageHide);
    globalThis.removeEventListener?.("pageshow", handlePageShow);
    pageListenersAdded = false;
  };

  const disconnectFromAssemblyAI = () => {
    if (assemblyAIService) {
      assemblyAIService.disconnect();
      assemblyAIService = null;
    }
    if (liveDrain) {
      liveDrain.service.disconnect();
      liveDrain = null;
    }
  };

  const uploadChunk = async (chunk) => {
    if (!uploadService) {
      console.error("No upload service initialized");
      return;
    }

    try {
      isUploading.value = true;

      // Add chunk to upload service (now handles immediate upload internally)
      await uploadService.addChunk(chunk);

      // Update uploaded bytes from service
      uploadedBytes.value = uploadService.uploadedBytes;
    } catch (error) {
      console.error("Failed to upload chunk:", error);

      // Log chunk upload failure
      criticalLoggingService.logChunkUpload("failed", {
        audioFileId: activeRecording.value?.audioFileId,
        chunkSize: chunk?.size || 0,
        error: error.message,
        uploadedBytes: uploadService?.uploadedBytes || 0,
        totalBytes: uploadService?.totalBytes || 0,
      });

      // Don't throw - we'll retry on next chunk
    } finally {
      isUploading.value = false;
    }
  };

  const finalizeUpload = async () => {
    if (!uploadService) {
      console.error("No upload service initialized");
      return;
    }

    try {
      isUploading.value = true;

      // Check if this is a small recording that should be uploaded as single chunk
      if (
        uploadService.totalBytes > 0 &&
        uploadService.totalBytes < uploadService.chunkSize &&
        uploadService.uploadedBytes === 0
      ) {
        console.log(
          `[Recording Store] Small recording detected (${uploadService.totalBytes} bytes), uploading as single chunk`,
        );
        await uploadService.uploadAsingleChunk();
      } else {
        // Flush any buffered data first
        uploadService.flushBuffer();

        // Upload any remaining chunks
        if (uploadService.chunks.length > 0) {
          await uploadService.uploadPendingChunks();
        }
      }

      uploadedBytes.value = uploadService.uploadedBytes;

      console.log("Upload finalized successfully");
    } catch (error) {
      console.error("Failed to finalize upload:", error);
      throw error;
    } finally {
      isUploading.value = false;
    }
  };

  // Emergency upload for page refresh scenarios
  const emergencyUpload = async () => {
    if (!uploadService) {
      console.warn("No upload service for emergency upload");

      // Log missing upload service
      criticalLoggingService.logEmergencyUpload({
        audioFileId: activeRecording.value?.audioFileId,
        status: "failed",
        reason: "no_upload_service",
        uploadedBytes: 0,
        totalBytes: 0,
      });

      return;
    }

    try {
      console.log("[Recording Store] Emergency upload triggered");

      // Log emergency upload start
      criticalLoggingService.logEmergencyUpload({
        audioFileId: activeRecording.value?.audioFileId,
        status: "started",
        uploadedBytes: uploadService.uploadedBytes || 0,
        totalBytes: uploadService.totalBytes || 0,
        chunksLength: uploadService.chunks?.length || 0,
      });

      await uploadService.emergencyUpload();
      uploadedBytes.value = uploadService.uploadedBytes;

      console.log("[Recording Store] Emergency upload completed");

      // Log emergency upload completion with UI state
      criticalLoggingService.logEmergencyUpload({
        audioFileId: activeRecording.value?.audioFileId,
        status: "completed",
        uploadedBytes: uploadService.uploadedBytes || 0,
        totalBytes: uploadService.totalBytes || 0,
      });

      // If no bytes were uploaded, log UI state for analysis
      if ((uploadService.uploadedBytes || 0) === 0) {
        criticalLoggingService.logUIStateSnapshot("emergency_upload_no_data", {
          audioFileId: activeRecording.value?.audioFileId,
          reason: "emergency_upload_completed_with_zero_bytes",
          mediaRecorder: {
            hasMediaRecorder: hasMediaRecorder.value,
            state: audioRecorderService?.getState(),
            isRecording: isRecording.value,
            isPaused: isPaused.value,
          },
          store: {
            recordingDuration: recordingDuration.value,
            audioChunks: audioChunks.value.length,
            uploadedBytes: uploadedBytes.value,
            isUploading: isUploading.value,
            transcriptionSegments: transcriptionSegments.value.length,
          },
          uploadService: {
            hasUploadService: !!uploadService,
            uploadedBytes: uploadService?.uploadedBytes || 0,
            totalBytes: uploadService?.totalBytes || 0,
            chunksLength: uploadService?.chunks?.length || 0,
            bufferSize: uploadService?.bufferSize || 0,
            bufferLength: uploadService?.buffer?.length || 0,
          },
        });
      }
    } catch (error) {
      console.error("Failed to perform emergency upload:", error);

      // Log emergency upload failure
      criticalLoggingService.logError(error, {
        audioFileId: activeRecording.value?.audioFileId,
        context: "emergency_upload_store",
        uploadedBytes: uploadService?.uploadedBytes || 0,
        totalBytes: uploadService?.totalBytes || 0,
      });

      // Don't throw - this is best-effort data preservation
    }
  };

  const addTranscriptionSegment = (segment) => {
    // For v3 API, turnOrder identifies unique segments. AssemblyAI restarts
    // turn_order at 0 for each streaming session, so apply the current session's
    // offset to keep turns from a reconnected session distinct from (and after)
    // everything captured before the reconnect.
    const turnOrder =
      segment.turnOrder !== undefined
        ? turnOrderBase + segment.turnOrder
        : undefined;

    if (turnOrder !== undefined) {
      // Find existing segment with same turnOrder
      const existingIndex = transcriptionSegments.value.findIndex(
        (s) => s.turnOrder === turnOrder,
      );

      // A finished turn is never downgraded: a stray earlier version of it
      // (e.g. the unformatted end-of-turn copy) must not replace the
      // formatted text.
      if (
        existingIndex >= 0 &&
        transcriptionSegments.value[existingIndex].isFinal &&
        !segment.isFinal
      ) {
        return;
      }

      if (existingIndex >= 0) {
        // Update existing segment
        transcriptionSegments.value[existingIndex] = {
          ...transcriptionSegments.value[existingIndex],
          text: segment.text,
          timestamp: segment.timestamp || Date.now(),
          isFinal: segment.isFinal || false,
        };
      } else {
        // Add new segment
        transcriptionSegments.value.push({
          text: segment.text,
          timestamp: segment.timestamp || Date.now(),
          isFinal: segment.isFinal || false,
          turnOrder,
        });
      }

      // Update pending segments similarly
      const pendingIndex = pendingSegments.value.findIndex(
        (s) => s.turnOrder === turnOrder,
      );
      const segmentData = {
        text: segment.text,
        timestamp: segment.timestamp || Date.now(),
        isFinal: segment.isFinal || false,
        turnOrder,
      };

      if (pendingIndex >= 0) {
        pendingSegments.value[pendingIndex] = segmentData;
      } else {
        pendingSegments.value.push(segmentData);
      }
    } else {
      // Fallback for v2 API or segments without turnOrder
      pendingSegments.value.push({
        text: segment.text,
        timestamp: segment.timestamp || Date.now(),
        isFinal: segment.isFinal || false,
        turnOrder:
          transcriptionSegments.value.length + pendingSegments.value.length,
      });

      transcriptionSegments.value.push(segment);
    }
  };

  // Live-preview segments are synced to the API through ONE chain, so an older
  // request can never land after (or be requeued over) a newer one. Each job
  // captures the recording generation and file; a job for an old generation
  // does nothing. Periodic syncs send only final turns; partials wait until
  // they turn final or stop sends everything as final.
  const SEGMENT_SYNC_TIMEOUT_MS = 10000;
  // Stop's whole budget for preview segments: waiting behind a sync in flight
  // plus its own final request. When it runs out, everything still queued or
  // in flight is retired (syncCutoff) and the stop goes on to save the audio.
  const STOP_SYNC_BUDGET_MS = 15000;
  let syncCutoff = 0;
  // The request in flight, so stop's cutoff (or reset) can cancel it.
  let syncAbort = null;

  const withTimeout = (promise, ms, label) => {
    let timer;
    return Promise.race([
      Promise.resolve(promise).finally(() => clearTimeout(timer)),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
      }),
    ]);
  };

  const isStaleSync = (job) => job.gen !== liveGen || job.cutoff !== syncCutoff;

  const runSegmentSync = async (job) => {
    const { audioFileId, forceFinal } = job;
    // A job from a reset/replaced recording, or retired by stop's cutoff,
    // selects nothing and sends nothing.
    if (isStaleSync(job) || !audioFileId) return;
    const batch = pendingSegments.value.filter((s) => forceFinal || s.isFinal);
    if (batch.length === 0) return;
    const sent = new Set(batch);
    pendingSegments.value = pendingSegments.value.filter((s) => !sent.has(s));
    const payload = batch.map((s) =>
      forceFinal ? { ...s, isFinal: true } : s,
    );

    // The API wrapper has no timeout of its own: abort the request (not just
    // stop waiting for it) at the deadline, so a late write can't land after
    // the next sync or after /stop.
    const controller = new AbortController();
    syncAbort = controller;
    const deadline = setTimeout(
      () => controller.abort(),
      SEGMENT_SYNC_TIMEOUT_MS,
    );
    try {
      const { error } =
        (await withTimeout(
          addTranscriptionSegments(audioFileId, payload, {
            signal: controller.signal,
          }),
          SEGMENT_SYNC_TIMEOUT_MS,
          "Segment sync",
        )) || {};
      if (error) {
        throw new Error(error.message || "Failed to sync segments");
      }
    } catch (error) {
      console.error("Failed to sync transcription segments:", error);
      if (isStaleSync(job)) return; // that recording is gone, or stop retired it
      // Requeue, unless a newer version of the same turn is already pending.
      const pendingTurns = new Set(
        pendingSegments.value.map((s) => s.turnOrder),
      );
      const retry = batch.filter((s) => !pendingTurns.has(s.turnOrder));
      pendingSegments.value = [...retry, ...pendingSegments.value];
    } finally {
      clearTimeout(deadline);
      if (syncAbort === controller) syncAbort = null;
    }
  };

  // Retire every queued or running sync (stop's cutoff, reset).
  const retireSegmentSyncs = () => {
    syncCutoff++;
    syncAbort?.abort();
    syncAbort = null;
  };

  const enqueueSegmentSync = (job) => {
    segmentSyncChain = segmentSyncChain
      .then(() => runSegmentSync(job))
      .catch((error) => {
        console.error("Segment sync chain error:", error);
      });
    return segmentSyncChain;
  };

  const syncTranscriptionSegments = (forceFinal = false) =>
    enqueueSegmentSync({
      gen: liveGen,
      cutoff: syncCutoff,
      audioFileId: activeRecording.value?.audioFileId,
      forceFinal,
    });

  // Stop (periodic syncs are already stopped): queue a final sync of
  // everything pending, as final, behind any sync in flight — so ordering
  // holds — and give the whole thing STOP_SYNC_BUDGET_MS. Then retire whatever
  // is left. Never throws: preview segments never block saving the audio.
  const flushTranscriptionSegmentsForStop = async ({ audioFileId, gen }) => {
    transcriptionSegments.value.forEach((s) => {
      s.isFinal = true;
    });
    const done = enqueueSegmentSync({
      gen,
      cutoff: syncCutoff,
      audioFileId,
      forceFinal: true,
    });
    let budgetTimer;
    await Promise.race([
      done,
      new Promise((resolve) => {
        budgetTimer = setTimeout(resolve, STOP_SYNC_BUDGET_MS);
      }),
    ]);
    clearTimeout(budgetTimer);
    // Only if the store still belongs to this recording: a newer recording's
    // syncs are not this stop's to retire.
    if (gen === liveGen) retireSegmentSyncs();
  };

  // A new streaming session started: turns from earlier sessions can never
  // be updated again, so their last text is final.
  const finalizeEarlierSessionSegments = () => {
    transcriptionSegments.value.forEach((s) => {
      s.isFinal = true;
    });
    pendingSegments.value.forEach((s) => {
      s.isFinal = true;
    });
  };

  const startSegmentSync = () => {
    // Sync segments every 5 seconds
    segmentSyncInterval = setInterval(() => {
      syncTranscriptionSegments();
    }, 5000);
  };

  const startHeartbeat = () => {
    // Send heartbeat every 5 seconds to check limits
    heartbeatInterval = setInterval(async () => {
      if (!isRecording.value || !activeRecording.value) return;

      try {
        const { data, error } = await sendRecordingHeartbeat(
          activeRecording.value.audioFileId,
          recordingDuration.value,
        );

        if (error) {
          console.error("[Recording Store] Heartbeat error:", error);
          return;
        }

        if (data) {
          // Update remaining seconds
          recordingLimits.value.remainingSeconds = data.remainingSeconds || 0;

          // Check for warning thresholds
          const remaining = data.remainingSeconds;
          const maxDuration = recordingLimits.value.maxDurationSeconds;
          const isPaidUser = maxDuration > 600; // Paid users have 5 hour (18000s) limit

          if (isPaidUser) {
            // For paid users with 5-hour limit, use different warning thresholds
            // 10 minutes warning
            if (
              remaining <= 600 &&
              remaining > 300 &&
              !warningShown.value.twoMinutes
            ) {
              warningShown.value.twoMinutes = true;
              globalThis.dispatchEvent(
                new CustomEvent("recording-warning", {
                  detail: {
                    remainingSeconds: remaining,
                    message: "10 minutes of recording time remaining",
                  },
                }),
              );
            }

            // 5 minutes warning
            if (
              remaining <= 300 &&
              remaining > 60 &&
              !warningShown.value.oneMinute
            ) {
              warningShown.value.oneMinute = true;
              globalThis.dispatchEvent(
                new CustomEvent("recording-warning", {
                  detail: {
                    remainingSeconds: remaining,
                    message: "5 minutes of recording time remaining",
                  },
                }),
              );
            }

            // 1 minute warning
            if (
              remaining <= 60 &&
              remaining > 0 &&
              !warningShown.value.thirtySeconds
            ) {
              warningShown.value.thirtySeconds = true;
              globalThis.dispatchEvent(
                new CustomEvent("recording-warning", {
                  detail: {
                    remainingSeconds: remaining,
                    message: "1 minute of recording time remaining!",
                  },
                }),
              );
            }
          } else {
            // For free users with 10-minute limit, use original thresholds
            // 2 minutes warning
            if (
              remaining <= 120 &&
              remaining > 60 &&
              !warningShown.value.twoMinutes
            ) {
              warningShown.value.twoMinutes = true;
              globalThis.dispatchEvent(
                new CustomEvent("recording-warning", {
                  detail: {
                    remainingSeconds: remaining,
                    message: "2 minutes of recording time remaining",
                  },
                }),
              );
            }

            // 1 minute warning
            if (
              remaining <= 60 &&
              remaining > 30 &&
              !warningShown.value.oneMinute
            ) {
              warningShown.value.oneMinute = true;
              globalThis.dispatchEvent(
                new CustomEvent("recording-warning", {
                  detail: {
                    remainingSeconds: remaining,
                    message: "1 minute of recording time remaining",
                  },
                }),
              );
            }

            // 30 seconds warning
            if (
              remaining <= 30 &&
              remaining > 0 &&
              !warningShown.value.thirtySeconds
            ) {
              warningShown.value.thirtySeconds = true;
              globalThis.dispatchEvent(
                new CustomEvent("recording-warning", {
                  detail: {
                    remainingSeconds: remaining,
                    message: "30 seconds of recording time remaining!",
                  },
                }),
              );
            }
          }

          // Check if we should stop
          if (data.shouldStop) {
            console.log(
              "[Recording Store] Recording limit reached:",
              data.reason,
            );
            // Auto-stop the recording — must be the enum value, not truthy
            await stopRecording("limit_reached");
            // Emit event for UI notification
            globalThis.dispatchEvent(
              new CustomEvent("recording-limit-reached", {
                detail: { reason: data.reason },
              }),
            );
          }
        }
      } catch (error) {
        console.error("[Recording Store] Failed to send heartbeat:", error);
      }
    }, 5000);
  };

  const startTimers = () => {
    // Duration timer
    durationInterval = setInterval(() => {
      if (isRecording.value) {
        recordingDuration.value++;
      }
    }, 1000);

    // Audio level monitoring is now handled by AudioRecorderService
    // through the onAudioLevel callback
  };

  const stopTimers = () => {
    if (durationInterval) {
      clearInterval(durationInterval);
      durationInterval = null;
    }

    if (segmentSyncInterval) {
      clearInterval(segmentSyncInterval);
      segmentSyncInterval = null;
    }

    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };

  const cleanup = () => {
    stopTimers();
    // Don't stop recording or clear data, just timers
  };

  const reset = () => {
    // Full reset. A new generation drops every late callback and queued sync
    // from the recording being reset.
    liveGen++;
    retireSegmentSyncs();
    stopTimers();
    removePageLifecycleListeners();
    pageSuspended = false;
    languageHandoff = null;
    liveLanguage = null;
    nextLiveSessionIndex = 0;
    activeRecording.value = null;
    isRecording.value = false;
    isPaused.value = false;
    recordingDuration.value = 0;
    currentAudioLevel.value = 0;
    transcriptionSegments.value = [];
    currentInterimText.value = "";
    pendingSegments.value = [];
    audioChunks.value = [];
    uploadedBytes.value = 0;
    hasMediaRecorder.value = false; // Reset MediaRecorder flag
    isReconnectingTranscription.value = false;
    turnOrderBase = 0;

    // Audio cleanup is handled by AudioRecorderService

    // Cancel any pending uploads
    if (uploadService) {
      uploadService.cancel();
      uploadService = null;
    }

    disconnectFromAssemblyAI();
  };

  return {
    // State
    activeRecording,
    isRecording,
    isPaused,
    recordingDuration,
    currentAudioLevel,
    transcriptionSegments,
    currentInterimText,
    hasActiveRecording,
    hasMediaRecorder, // Export the MediaRecorder flag
    recordingStatus,
    uploadProgress,
    uploadedBytes,
    isUploading,
    recordingLimits,
    isReconnectingTranscription,

    // Methods
    rememberLiveLanguage,
    setActiveRecording,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    addTranscriptionSegment,
    emergencyUpload,
    cleanup,
    reset,
  };
});
